/**
 * Verse analysis API client — KJV vs NASB Strong's verdict per verse.
 *
 * Verdicts are cached in localStorage under a versioned prefix.
 * DATA_VERSION must be bumped whenever variant-analysis.sqlite is
 * regenerated (scripts/generate-variant-analysis.sh) so stale verdicts
 * self-purge.
 */

import type {
  BibleLocation,
  ChapterAnalysisResponse,
  ProblemListEntry,
  VerseAnalysisResponse,
} from '@skjv/common';
import {
  ChapterAnalysisResponseSchema,
  ProblemListResponseSchema,
  VerseAnalysisResponseSchema,
} from '@skjv/common';
import { apiPath } from '@utils/api-base';

const DATA_VERSION = 'v2';
const CACHE_PREFIX = `verse-analysis:${DATA_VERSION}:`;
const STALE_PREFIX = 'verse-analysis:';

let hasSweptStale = false;

/** Remove cache entries written against an older DB generation */
function sweepStaleVersions(): void {
  if (hasSweptStale) return;
  hasSweptStale = true;
  const stale: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STALE_PREFIX) && !key.startsWith(CACHE_PREFIX)) stale.push(key);
  }
  stale.forEach((key) => localStorage.removeItem(key));
}

/** Remove half the current-version entries when storage quota is hit */
function clearOldCacheEntries(): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) keys.push(key);
  }
  keys.slice(0, Math.ceil(keys.length / 2)).forEach((key) => localStorage.removeItem(key));
}

/**
 * Fetch the analysis verdict for a verse (0-based BibleLocation).
 * Returns null on network failure — callers show nothing.
 */
export async function fetchVerseAnalysis(
  location: BibleLocation,
): Promise<VerseAnalysisResponse | null> {
  sweepStaleVersions();

  const { testamentIndex: t, bookIndex: b, chapterIndex: c, verseIndex: v } = location;
  const key = `${CACHE_PREFIX}${t}-${b}-${c}-${v}`;

  const cached = localStorage.getItem(key);
  if (cached !== null) {
    try {
      return VerseAnalysisResponseSchema.parse(JSON.parse(cached));
    } catch {
      localStorage.removeItem(key);
    }
  }

  try {
    const response = await fetch(
      apiPath(`v1/verse-analysis/${t + 1}/${b + 1}/${c + 1}/${v + 1}`),
    );
    if (!response.ok) return null;

    const verdict = VerseAnalysisResponseSchema.parse(await response.json());
    try {
      localStorage.setItem(key, JSON.stringify(verdict));
    } catch {
      clearOldCacheEntries();
      try {
        localStorage.setItem(key, JSON.stringify(verdict));
      } catch {
        // cache full — serve uncached
      }
    }
    return verdict;
  } catch {
    return null;
  }
}

/**
 * Per-verse problem scores (1-based verse → score) for a chapter
 * (0-based location). Feeds the reader's verse-number rings.
 * Returns null on failure — callers skip decoration.
 */
export async function fetchChapterAnalysis(
  location: BibleLocation,
): Promise<ChapterAnalysisResponse | null> {
  sweepStaleVersions();

  const { testamentIndex: t, bookIndex: b, chapterIndex: c } = location;
  const key = `${CACHE_PREFIX}ch:${t}-${b}-${c}`;

  const cached = localStorage.getItem(key);
  if (cached !== null) {
    try {
      return ChapterAnalysisResponseSchema.parse(JSON.parse(cached));
    } catch {
      localStorage.removeItem(key);
    }
  }

  try {
    const response = await fetch(
      apiPath(`v1/verse-analysis/chapter/${t + 1}/${b + 1}/${c + 1}`),
    );
    if (!response.ok) return null;

    const chapter = ChapterAnalysisResponseSchema.parse(await response.json());
    try {
      localStorage.setItem(key, JSON.stringify(chapter));
    } catch {
      clearOldCacheEntries();
    }
    return chapter;
  } catch {
    return null;
  }
}

let problemListPromise: Promise<ProblemListEntry[]> | null = null;

/**
 * The full problems list (score > 0, most severe first), memoized for the
 * session — ~7k compact rows, fetched once for the side-panel Problems tab.
 */
export function fetchProblemList(): Promise<ProblemListEntry[]> {
  problemListPromise ??= (async () => {
    const response = await fetch(apiPath('v1/verse-analysis/problems'));
    if (!response.ok) throw new Error(`problems list: ${response.status}`);
    return ProblemListResponseSchema.parse(await response.json()).problems;
  })().catch((err) => {
    problemListPromise = null; // allow retry on next open
    throw err;
  });
  return problemListPromise;
}
